import HttpService from '@/common/services/HttpService'
import {
  type Interaction,
  type InteractionCustomDTOV3,
  type InteractionCustom,
  type InteractionCustomCreateOrUpdate,
  type InteractionDetailsDTOV1,
  type InteractionDTOV4,
  type ProcessConfidentialDTOV3,
  type InteractionCreateDTOV4,
  type InteractionCreateUpdateDTOV4,
  type InteractionUpdateRequestV4,
} from '@/common/types/interaction'

import { PrefixTranslationsEnum } from '@/common/types/i18n'
import { includeTranslationsInItems } from '@/common/helpers/services'
import { type InteractionTypeDTOV1, type InteractionFieldConfig } from '@/common/types/interactionProperties'
import { type AxiosRequestConfig } from 'axios'

const ServiceV3CustomTypesPath = 'api/v3/interactions/custom-types'

export const getInteractionTypes = async (): Promise<InteractionTypeDTOV1[]> => {
  return (await HttpService.getData<InteractionTypeDTOV1[]>('/api/v1/interactions/types')) || []
}

export const getCustomInteractionTypes = async (): Promise<InteractionCustomDTOV3[]> => {
  return (
    (await HttpService.getData<InteractionCustomDTOV3[]>(ServiceV3CustomTypesPath, {
      params: {
        fetchAll: true,
      },
    })) || []
  )
}

export const getLatestMessagingInteraction = async (
  caseId: number,
  { raw = true }: Partial<AxiosRequestConfig> = {},
): Promise<InteractionDTOV4 | undefined> => {
  return await HttpService.getData<InteractionDTOV4>('/api/v4/interactions/messaging/latest', {
    params: {
      'case-id': caseId,
    },
    raw,
  })
}

export const getBulkInteractions = async (ids?: number[], { includes, expands }: InteractionFieldConfig = {}): Promise<InteractionDetailsDTOV1[]> => {
  const params = {
    include: includes?.size ? [...includes].join() : undefined,
    expand: expands?.size ? [...expands].join() : undefined,
  }
  return HttpService.postData<InteractionDetailsDTOV1[]>(`/api/v1/bulk-interactions/fetch`, { ids }, { params })
}

export const updateInteractionSensitiveContent = async (id: number, content: string): Promise<ProcessConfidentialDTOV3> => {
  return HttpService.postData<ProcessConfidentialDTOV3>(`/api/v3/interactions/${id}/process-confidential`, {
    content,
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isInteraction(entity: any): entity is Interaction {
  return (entity as Interaction).interactionDate !== undefined
}

export const getAllInteractionsCustomTypes = async (): Promise<InteractionCustom[]> => {
  return includeTranslationsInItems(
    () =>
      HttpService.getData<InteractionCustomDTOV3[]>(ServiceV3CustomTypesPath, {
        params: {
          fetchAll: true,
        },
      }),
    PrefixTranslationsEnum.INTERACTION,
    'id',
  )
}

export const getInteractionCustomType = (id: UUID) => {
  return HttpService.getData<InteractionCustomDTOV3>(`${ServiceV3CustomTypesPath}/${id}`)
}

export const createInteractionCustomType = async (interaction: InteractionCustomCreateOrUpdate) => {
  return HttpService.post(ServiceV3CustomTypesPath, interaction)
}

export const createDraftInteraction = async (interaction: InteractionCreateUpdateDTOV4, config: AxiosRequestConfig = {}) => {
  return await HttpService.postData<InteractionCreateDTOV4>('/api/v4/interactions', interaction, config)
}

export const updateInteractionV4 = async (id: number, params: InteractionUpdateRequestV4, config: AxiosRequestConfig = {}) => {
  return HttpService.patch(`/api/v4/interactions/${id}`, params, config)
}

export const sendInteractionTemplate = async (interactionId: number, templateId: number, config: AxiosRequestConfig = {}) => {
  return HttpService.put(`/api/v4/interactions/${interactionId}/send-template/${templateId}`, {}, config)
}

export const updateInteractionCustomType = async (id: UUID, interaction: InteractionCustomCreateOrUpdate) => {
  return HttpService.put(`${ServiceV3CustomTypesPath}/${id}`, interaction)
}

export const handleInteractionCustomActivation = (interactionCustom: InteractionCustomDTOV3, active: boolean) => {
  return HttpService.put(`${ServiceV3CustomTypesPath}/${interactionCustom.id}`, {
    ...interactionCustom,
    active,
  })
}
